import { JSX } from 'react'

/**
 * お問い合わせのステータスを日本語ラベルに変換
 * @param status - ステータス ('in_progress' | 'irogami_answered' | 'waiting_admin' | 'completed')
 * @returns 日本語ラベル
 *
 * 表示ラベル:
 * - waiting_admin: イロガミ回答待ち
 * - waiting_school: イロガミ回答済み
 * - in_progress: イロガミ対応中
 * - completed: 完了
 */
export const getContactStatusLabel = (
  status: 'in_progress' | 'irogami_answered' | 'waiting_admin' | 'completed'
): string => {
  switch (status) {
    case 'in_progress':
      return 'イロガミ対応中'
    case 'irogami_answered':
      return 'イロガミ回答済み'
    case 'waiting_admin':
      return 'イロガミ回答待ち'
    case 'completed':
      return '完了'
    default:
      return status
  }
}

/**
 * お問い合わせのステータスの説明を取得
 * @param status - ステータス ('in_progress' | 'irogami_answered' | 'waiting_admin' | 'completed')
 * @returns ステータスの説明
 */
export const getContactStatusDescription = (
  status: 'in_progress' | 'irogami_answered' | 'waiting_admin' | 'completed'
): string => {
  switch (status) {
    case 'in_progress':
      return '学校からのお問い合わせを受付済みです。イロガミが対応中です。'
    case 'irogami_answered':
      return 'イロガミからの回答があります。'
    case 'waiting_admin':
      return 'イロガミからの回答をお待ちしています。'
    case 'completed':
      return 'このお問い合わせは完了しています。'
    default:
      return ''
  }
}

interface ContactStatusBadgeProps {
  status: 'in_progress' | 'irogami_answered' | 'waiting_admin' | 'completed'
}

/**
 * お問い合わせステータスバッジコンポーネント
 * お問い合わせのステータスをバッジ形式で表示
 */
export const ContactStatusBadge = ({ status }: ContactStatusBadgeProps): JSX.Element => {
  const getStatusStyle = () => {
    switch (status) {
      case 'in_progress':
        // 対応中 - オレンジ系（注意が必要）
        return {
          backgroundColor: '#fff3e0',
          color: '#e65100',
        }
      case 'irogami_answered':
        // 学校回答待ち - 青系（学校からの回答待ち）
        return {
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
        }
      case 'waiting_admin':
        // 管理者回答待ち - 紫系（管理者の対応待ち、緊急度が高い）
        return {
          backgroundColor: '#f3e5f5',
          color: '#7b1fa2',
        }
      case 'completed':
        // 対応完了 - 緑系（完了）
        return {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
        }
      default:
        return {
          backgroundColor: '#e9ecef',
          color: '#495057',
        }
    }
  }

  const style = getStatusStyle()

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: '3px',
        fontSize: '0.65rem',
        fontWeight: 500,
        lineHeight: '1.2',
        backgroundColor: style.backgroundColor,
        color: style.color,
        whiteSpace: 'nowrap',
      }}
    >
      {getContactStatusLabel(status)}
    </span>
  )
}
